/**
 * The Roster view: three filter tiles, a search box, and the three lists.
 *
 * Filtering is local to what the roster endpoint already returned. The whole
 * roster is one request and a site is tens of people, not thousands, so
 * round-tripping a filter would cost a network hop to remove rows the browser
 * is already holding — and it would make "export what I am looking at"
 * dependent on a second request agreeing with the first.
 */
import { Search } from "lucide-react";
import { useMemo } from "react";
import type { ComplianceStatus, CrewMember, CrewRoster } from "@gb/contracts";
import { CrewAvatar, toneFor } from "@/components/gb/crew-avatar";
import { DataTable } from "@/components/gb/data-table";
import { SectionLabel } from "@/components/gb/section-label";
import { StatCard } from "@/components/gb/stat-card";
import { StatusPill } from "@/components/gb/status-pill";
import { Input } from "@/components/ui/input";
import { formatDate } from "@/lib/format";

/** The three tiles, and what each one keeps. */
export type RosterFilter = "all" | "valid" | "expiring" | "missing";

function matchesFilter(status: ComplianceStatus, filter: RosterFilter): boolean {
  if (filter === "all") return true;
  if (filter === "valid") return status === "valid";
  if (filter === "expiring") return status === "expiring";
  // The red tile groups missing and expired, as the design does.
  return status === "missing" || status === "expired";
}

function matchesSearch(person: CrewMember, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    person.name.toLowerCase().includes(q) ||
    person.roleLabel.toLowerCase().includes(q) ||
    person.id.toLowerCase().includes(q)
  );
}

/**
 * What the roster is showing right now — the same object the CSV export uses,
 * so the file and the screen cannot disagree.
 */
export function filterRoster(
  roster: CrewRoster,
  filter: RosterFilter,
  query: string,
): { onSite: CrewMember[]; onboarding: CrewMember[]; offSite: CrewMember[] } {
  const onSite = roster.onSite.filter(
    (p) => matchesFilter(p.overall, filter) && matchesSearch(p, query),
  );
  // Onboarding and off-site crew have no place in a compliance-state filter —
  // they are outside the tiles' counts — so a tile selection hides those
  // sections rather than filtering them, exactly as the design does.
  const secondary = filter === "all";
  return {
    onSite,
    onboarding: secondary ? roster.onboarding.filter((p) => matchesSearch(p, query)) : [],
    offSite: secondary ? roster.offSite.filter((p) => matchesSearch(p, query)) : [],
  };
}

export function RosterTab({
  roster,
  filter,
  onFilterChange,
  search,
  onSearchChange,
  onClear,
  onSelect,
}: {
  roster: CrewRoster;
  filter: RosterFilter;
  onFilterChange: (next: RosterFilter) => void;
  search: string;
  onSearchChange: (next: string) => void;
  /** Clears tile and search in one URL write — two would clobber each other. */
  onClear: () => void;
  onSelect: (person: CrewMember) => void;
}) {
  const shown = useMemo(
    () => filterRoster(roster, filter, search),
    [roster, filter, search],
  );

  const toggle = (next: RosterFilter) => () =>
    onFilterChange(filter === next ? "all" : next);

  return (
    <>
      <div className="mb-[18px] flex gap-3.5">
        <StatCard
          tone="ok"
          count={roster.breakdown.valid}
          label="Valid"
          selected={filter === "valid"}
          onClick={toggle("valid")}
        />
        <StatCard
          tone="warn"
          count={roster.breakdown.expiring}
          label="Expiring < 30d"
          selected={filter === "expiring"}
          onClick={toggle("expiring")}
        />
        <StatCard
          tone="danger"
          count={roster.breakdown.missing}
          label="Missing / expired"
          selected={filter === "missing"}
          onClick={toggle("missing")}
        />
      </div>

      <div className="mb-[18px] flex items-center gap-3.5">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute top-1/2 left-3.5 flex -translate-y-1/2 text-muted">
            <Search size={17} aria-hidden />
          </span>
          <Input
            withIcon
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search crew or role"
            aria-label="Search crew or role"
          />
        </div>
        {(filter !== "all" || search !== "") && (
          <button
            type="button"
            onClick={onClear}
            className="cursor-pointer font-display text-[0.82rem] font-semibold tracking-[0.02em] whitespace-nowrap text-brand uppercase"
          >
            Clear filter
          </button>
        )}
      </div>

      {shown.onboarding.length > 0 && (
        <>
          <SectionLabel className="mx-0.5 mt-3.5 mb-2.5">
            Onboarding · {shown.onboarding.length}
          </SectionLabel>
          <DataTable
            className="mb-2"
            rows={shown.onboarding}
            getRowKey={(p) => p.id}
            onSelect={onSelect}
            headerClassName="hidden"
            emptyMessage="Nobody is onboarding."
            columns={[
              {
                key: "avatar",
                width: 40,
                render: (p) => <CrewAvatar initials={p.initials} tone="amber" />,
              },
              {
                key: "name",
                render: (p) => (
                  <>
                    <div className="truncate text-[0.95rem] font-bold">{p.name}</div>
                    <div className="truncate text-[0.82rem] text-muted">
                      {p.roleLabel} · {missingCount(p)}
                    </div>
                  </>
                ),
              },
              {
                key: "status",
                width: 140,
                render: () => <StatusPill status="expiring" label="Pending" />,
              },
              { key: "chevron", width: 18, render: () => <Chevron /> },
            ]}
          />
        </>
      )}

      <SectionLabel
        className="mx-0.5 mt-[18px] mb-2.5"
        trailing={`${shown.onSite.length} shown`}
      >
        {filter === "all" && search === "" ? "Active roster" : "Filtered"}
      </SectionLabel>
      <DataTable
        rows={shown.onSite}
        getRowKey={(p) => p.id}
        onSelect={onSelect}
        emptyMessage="No crew match."
        columns={[
          {
            key: "avatar",
            width: 40,
            render: (p) => (
              <CrewAvatar initials={p.initials} tone={toneFor(p.overall)} />
            ),
          },
          {
            key: "name",
            header: "Name & role",
            render: (p) => (
              <>
                <div className="truncate text-[0.95rem] font-bold">{p.name}</div>
                <div className="truncate text-[0.82rem] text-muted">
                  {p.roleLabel} · {detailOf(p)}
                </div>
              </>
            ),
          },
          {
            key: "status",
            header: "Status",
            width: 140,
            render: (p) => <StatusPill status={p.overall} />,
          },
          { key: "chevron", width: 18, render: () => <Chevron /> },
        ]}
      />

      {shown.offSite.length > 0 && (
        <>
          <SectionLabel className="mx-0.5 mt-[22px] mb-2.5">Off-site crew</SectionLabel>
          <DataTable
            rows={shown.offSite}
            getRowKey={(p) => p.id}
            onSelect={onSelect}
            headerClassName="hidden"
            emptyMessage="Nobody is off site."
            columns={[
              {
                key: "avatar",
                width: 40,
                render: (p) => <CrewAvatar initials={p.initials} tone="slate" />,
              },
              {
                key: "name",
                render: (p) => (
                  <>
                    <div className="truncate text-[0.95rem] font-bold">{p.name}</div>
                    <div className="truncate text-[0.82rem] text-muted">
                      {p.roleLabel}
                    </div>
                  </>
                ),
              },
              {
                key: "status",
                width: 140,
                render: () => (
                  <span className="inline-flex items-center rounded-full border-[1.5px] border-border bg-subtle px-2.5 py-1 text-[0.76rem] font-semibold text-muted">
                    Off site
                  </span>
                ),
              },
              { key: "chevron", width: 18, render: () => <Chevron /> },
            ]}
          />
        </>
      )}
    </>
  );
}

function Chevron() {
  return (
    <span className="flex text-muted" aria-hidden>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m9 18 6-6-6-6" />
      </svg>
    </span>
  );
}

/** The roster row's second line: the nearest thing to a problem. */
function detailOf(person: CrewMember): string {
  const worst = person.perDoc.find((d) => d.status === person.overall);
  if (!worst) return "all documents valid";
  if (worst.status === "missing") return `${worst.label} not on file`;
  if (worst.status === "expired") {
    return `${worst.label} expired ${Math.abs(worst.days ?? 0)}d ago`;
  }
  if (worst.status === "expiring") {
    return `${worst.label} — ${worst.days ?? 0}d left`;
  }
  // Everything is valid, so the soonest expiry is the useful thing to show.
  const soonest = [...person.perDoc]
    .filter((d) => d.days !== null)
    .sort((a, b) => (a.days ?? 0) - (b.days ?? 0))[0];
  return soonest?.expiryDate
    ? `next renewal ${formatDate(soonest.expiryDate)}`
    : "all documents valid";
}

function missingCount(person: CrewMember): string {
  const outstanding = person.perDoc.filter((d) => d.status !== "valid").length;
  return outstanding === 1 ? "1 document outstanding" : `${outstanding} documents outstanding`;
}
